/**
 * The two places the app admits a card has not been signed for.
 *
 * Both exist because of one rule: a signature is the VALIDATION of a card, and
 * nothing downstream may read an unvalidated one — not a dollar and not a point.
 * `computeAll` enforces that by scoring the ledger AND the championship seeding
 * from signed settlements only.
 *
 * The consequence is that the week can look finished while the flights are still
 * standing on part of it, and that is not a state to hide. So instead of quietly
 * showing a projection off half the evidence, these two name the cards that are
 * missing and hand you a way to go and sign them.
 *
 *   UnsignedCards  the feed list — cards that outlived their own day
 *   SeedingBlocked what is holding the championship draw up
 */

import type { Tournament } from '../engine/types'
import { clockTime } from '../engine/clock'
import { outstandingCards, playerName, todayLocal, type OutstandingCard } from '../select'

/**
 * Cards that were played on yesterday and never signed for.
 *
 * This sits above the birdies because it is the one thing in the feed that is
 * asking for something rather than reporting something. Signing is what releases
 * money — `computeAll` will not settle a match until every group in its round has
 * signed — so a single foursome that walked off unsigned is quietly holding up
 * three other men's payout, and nothing anywhere said so. The live board keeps
 * showing their scores the whole time, which is exactly why nobody notices.
 *
 * Only shown once the day has turned over. During the round itself an unsigned
 * card is not a problem, it is a round in progress, and a warning that is on all
 * afternoon is one nobody reads by Tuesday.
 */
export function UnsignedCards({
  doc,
  cards,
  me,
  onSign,
}: {
  doc: Tournament
  cards: OutstandingCard[]
  me: string
  onSign?: (roundId: string) => void
}) {
  if (cards.length === 0) return null

  return (
    <div className="border-b-2 border-signal">
      <div className="bg-signal text-paper px-4 py-2">
        <span className="bc-label bc-chip">
          {cards.length === 1 ? 'A card is unsigned' : `${cards.length} cards are unsigned`}
        </span>
      </div>
      <ul className="divide-y divide-rule">
        {cards.map((c) => {
          /*
           * Only actionable by somebody in that foursome — any one of them signs
           * for the whole group. For everybody else this is a name to chase at
           * breakfast, so it stays a plain row rather than pretending to be a
           * button that will not work.
           */
          const mine = c.playerIds.includes(me)
          const go = mine && onSign ? () => onSign(c.roundId) : undefined
          const Tag = go ? 'button' : 'div'
          return (
            <li key={`${c.roundId}|${c.groupId}`}>
              <Tag
                {...(go ? { onClick: go, type: 'button' as const } : {})}
                className="w-full text-left px-4 py-3 grid grid-cols-[minmax(0,1fr)_auto] gap-3 items-center bc-tap"
              >
                <span className="min-w-0">
                  <span className="bc-name text-sm block truncate">
                    {c.playerIds.map((p) => playerName(doc, p)).join(', ')}
                  </span>
                  <span className="text-[12px] text-mute block truncate">
                    {c.roundLabel} · {clockTime(c.teeTime)} · {c.holesIn} score
                    {c.holesIn === 1 ? '' : 's'} in
                  </span>
                </span>
                <span className="bc-label text-signal shrink-0">
                  {/*
                    Days, not a date. "Yesterday" is what somebody at breakfast
                    is actually reckoning in, and it is the number that says
                    whether this is a nag or an emergency.
                  */}
                  {mine ? 'Sign it' : c.daysLate === 1 ? 'Yesterday' : `${c.daysLate} days`}
                </span>
              </Tag>
            </li>
          )
        })}
      </ul>
      <p className="px-4 py-3 text-[13px] text-mute leading-snug">
        Nothing in a round pays out until every group in it has signed, so
        these are holding up the money for the whole group. Any one man in a
        foursome can sign for it.
      </p>
    </div>
  )
}

/**
 * Why the flights are not final yet.
 *
 * Shown wherever the championship draw appears, because the draw is the thing
 * being qualified. It used to be silently seeded off every settlement including
 * unsigned and half-played rounds, which meant the flights moved as scores
 * arrived and nothing said so — and a flight is worth a thousand dollars.
 *
 * Renders nothing once the week's cards are in, which is the normal state by
 * Wednesday. A notice that is up all week is one nobody reads.
 */
export function SeedingBlocked({
  doc,
  cards,
  onSign,
}: {
  doc: Tournament
  cards: OutstandingCard[]
  onSign?: (roundId: string) => void
}) {
  if (cards.length === 0) return null

  return (
    <div className="mx-4 mb-3 border-2 border-signal">
      <p className="bg-signal text-paper px-3 py-2 text-[13px] leading-snug">
        <b className="bc-label">Not the final draw.</b>{' '}
        {cards.length === 1 ? 'One card has' : `${cards.length} cards have`} not been
        signed for, so the points behind these flights are incomplete.
      </p>
      <ul className="divide-y divide-rule">
        {cards.map((c) => {
          const go = onSign ? () => onSign(c.roundId) : undefined
          const Tag = go ? 'button' : 'div'
          return (
            <li key={`${c.roundId}|${c.groupId}`}>
              <Tag
                {...(go ? { onClick: go, type: 'button' as const } : {})}
                className="w-full text-left px-3 py-2.5 grid grid-cols-[minmax(0,1fr)_auto] gap-3 items-center bc-tap"
              >
                <span className="min-w-0">
                  <span className="bc-name text-[13px] block truncate">{c.roundLabel}</span>
                  <span className="text-[12px] text-mute block truncate">
                    {clockTime(c.teeTime)} · {c.playerIds.map((p) => playerName(doc, p)).join(', ')}
                  </span>
                </span>
                {go && <span className="bc-label text-signal shrink-0">Sign &rsaquo;</span>}
              </Tag>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/**
 * The cards standing between today and a final championship draw.
 *
 * Every unsigned card that has been played on, regardless of which day it was —
 * the difference from the feed list. The feed only nags about yesterday, because
 * during a round an unsigned card is a round in progress. The draw gets no such
 * latitude: a card from this morning holds it up just as surely.
 *
 * The championship's OWN round is excluded, and that is not tidiness. It is the
 * thing being seeded, so it cannot be signed before it is played — listing it
 * would tell somebody that the draw is waiting on a card that can only exist
 * after the draw. Same reason `seedingComplete` skips it.
 */
export function seedingBlockers(
  doc: Tournament,
  scores: Parameters<typeof outstandingCards>[1],
  confirmations: Parameters<typeof outstandingCards>[2],
): OutstandingCard[] {
  // The FLIGHTED one. An ad-hoc card is unflighted stroke play, and picking it
  // up here would exempt the wrong round — leaving the championship's own
  // unsigned card listed as blocking the seeding it is the subject of.
  const champRound = doc.matches?.find(
    (m) => m.config.kind === 'strokeNet' && m.config.flighted,
  )?.roundId
  return outstandingCards(doc, scores, confirmations, todayLocal()).filter(
    (c) => c.roundId !== champRound,
  )
}
